import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  NgxDatatableModule,
  TableColumn,
  DatatableComponent as NgxDatatableComponent,
  SortPropDir,
} from '@swimlane/ngx-datatable';
import { DatatableComponent } from '@bo/ng-datatable';
import { HttpParams } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageChangedEvent, PaginationComponent, PaginationModule } from 'ngx-bootstrap/pagination';
import { EmployeeApiService } from '../../../_services/employee-service/api/employee-api.service';
import { SubscriptionApiService } from '../../../_services/subscription-service/api/subscription-api-service.service';
import { CardsApiService } from '../../../_services/cards-service/cards-api/cards-api.service';
import { TransactionsApiService } from '../../../_services/transactions-service/transactions-api.service';
import { updateParamsObject } from '../../utils/filter-utils';
import { calcShowPage } from '../../utils/general-utils';
import { CardTopupsApiService } from '../../../_services/topup-service/api/card-topups-api.service';

@Component({
  selector: 'app-table',
  imports: [NgxDatatableModule, PaginationModule, NgClass, NgTemplateOutlet, FormsModule],
  templateUrl: './app-table.component.html',
  styleUrl: './app-table.component.scss',
})
export class AppTableComponent<TTableData>
  extends DatatableComponent
  implements AfterContentChecked, AfterViewInit, OnChanges
{
  protected override router: Router;
  readonly route: ActivatedRoute;
  readonly cardsApiService = inject(CardsApiService);
  readonly subscriptionApiService = inject(SubscriptionApiService);
  readonly employeeApiService = inject(EmployeeApiService);
  readonly transactionsApiService = inject(TransactionsApiService);
  readonly cardTopupsApiService = inject(CardTopupsApiService);
  protected override cdr: ChangeDetectorRef;

  @Input({ required: true }) columns!: Array<TableColumn>;
  @Input({ required: true }) getDataCallback!: (queryParams: HttpParams) => Observable<TTableData>;
  @Input() emptyMessage?: string;
  @Input() override pageSize: number = 10;
  @Input() class: string | undefined;
  @Input() showPagination: boolean = true;
  @Input() rowDetailTemplate?: TemplateRef<{ row: unknown }>;
  @Input() rowDetailHeight: number = 50;
  @Input() canExpandRow?: (row: unknown) => boolean; // Function to determine if row can expand
  // When true, suppresses fetches from route query param changes (e.g. while
  // switching tabs). Triggers one fresh fetch when it flips back to false.
  @Input() suppressFetch: boolean = false;
  @Output() itemClicked: EventEmitter<TTableData> = new EventEmitter<TTableData>();
  @Output() dataLoaded: EventEmitter<number> = new EventEmitter<number>(); // Emits the data length when data loads

  @ViewChild('table', { static: false }) table!: NgxDatatableComponent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('expandCell', { static: true }) expandCell?: TemplateRef<any>;
  @ViewChild('paginator', { static: true }) paginator?: PaginationComponent;

  toggleExpandRow(row: unknown) {
    if (this.table?.rowDetail) {
      this.table.rowDetail.toggleExpandRow(row);
    }
  }

  // Track rows by a unique identifier for Angular 20 @for loop tracking requirement
  private rowTrackingCounter = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly rowTrackingMap = new WeakMap<any, number>();
  private expandColumnAdded = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowIdentity = (row: any) => {
    // Return a unique identifier: prioritize id, then exid
    if (row?.id !== undefined && row?.id !== null && row?.id !== '') {
      return `id-${String(row.id)}`;
    }
    if (row?.exid !== undefined && row?.exid !== null && row?.exid !== '') {
      return `exid-${String(row.exid)}`;
    }

    // For rows without proper IDs (like loading placeholders), assign a unique counter
    // Use WeakMap to track rows we've already seen
    if (!this.rowTrackingMap.has(row)) {
      this.rowTrackingMap.set(row, this.rowTrackingCounter++);
    }
    return `row-${this.rowTrackingMap.get(row)}`;
  };

  rowNumber!: number;
  totalShown!: number;

  // Current page number (1-based) derived from URL page offset (0-based)
  currentPage = (): number => {
    return this.getPageOffset() + 1;
  };

  queryParams = new HttpParams();

  // Important: don't remove "unused" fields in constructor.
  // Be sure that the naming of the service below EXACTLY matches that of which is being used in the "getData" function in the relevant component
  constructor() {
    const router = inject(Router);
    const route = inject(ActivatedRoute);
    const cdr = inject(ChangeDetectorRef);

    super(router, route, cdr);

    this.router = router;
    this.route = route;
    this.cdr = cdr;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const suppressFetchChange = changes['suppressFetch'];
    // When suppression is lifted (true -> false), trigger a single fresh fetch
    // with the current route params, since any fetch while suppressed was skipped.
    if (suppressFetchChange && !suppressFetchChange.firstChange && suppressFetchChange.previousValue === true) {
      this.reloadData();
    }
  }

  ngAfterViewInit(): void {
    // Ensure expand column is added after view is initialised
    this.addExpandColumn();
  }

  /**
   * Override parent's onDataLoaded hook to update pagination UI after data loads.
   * This ensures the paginator shows the correct page from the URL on reload.
   */
  protected override onDataLoaded(): void {
    super.onDataLoaded();
    this.updatePaginationUI();
    // Emit data length to parent component
    this.dataLoaded.emit(this.dataLength);
  }

  private updatePaginationUI(): void {
    if (this.paginator?.writeValue && typeof this.currentPage === 'function') {
      const currentPage = this.currentPage();
      // Use queueMicrotask to ensure it runs after current change detection cycle
      queueMicrotask(() => {
        this.paginator?.writeValue(currentPage);
        this.cdr.detectChanges();
      });
    }
  }

  ngAfterContentChecked(): void {
    this.calcShowPage();
    // Also try to add expand column during content checks
    this.addExpandColumn();
  }

  private addExpandColumn(): void {
    // Add expand column if row detail template is provided
    if (this.rowDetailTemplate && this.expandCell && this.columns.length > 0 && !this.expandColumnAdded) {
      const hasExpandColumn = this.columns.some((col) => col.prop === '$$expandColumn');

      if (!hasExpandColumn) {
        this.columns.unshift({
          prop: '$$expandColumn',
          name: '',
          width: 50,
          resizeable: false,
          sortable: false,
          draggable: false,
          canAutoResize: false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          cellTemplate: this.expandCell as any,
        });

        // Force change detection
        this.columns = [...this.columns];
        this.expandColumnAdded = true;
      }
    }
  }

  /**
   * Force-refresh table data using current URL query params.
   * Overrides the library refreshData() which incorrectly serializes ParamMap
   * as params=[object Object].
   */
  reloadData() {
    const activeParams = this.route.snapshot.queryParams;
    let params = Object.keys(activeParams).length ? new HttpParams().appendAll(activeParams) : new HttpParams();

    // Ensure page / page_size defaults exist when URL has none yet
    if (!params.has('page')) {
      params = params.set('page', String(this.getPageOffset() + 1));
    }
    if (!params.has('page_size')) {
      params = params.set('page_size', String(this.pageSize));
    }

    this.getTableData(params);
  }

  getTableDataLength() {
    return this.dataLength;
  }

  calcShowPage() {
    const result = calcShowPage(this.getPageOffset(), this.pageSize, this.dataLength);
    this.rowNumber = result.rowNumber;
    this.totalShown = result.totalShown;
  }

  onPageChange(event: PageChangedEvent) {
    // Convert ngx-bootstrap PageChangedEvent to ngx-datatable page event
    const datatableEvent = {
      offset: event.page - 1, // Convert 1-based page to 0-based offset
      limit: event.itemsPerPage,
    };
    this.onChange(datatableEvent);
    this.calcShowPage();
  }

  // function to return list of numbers from 0 to n-1
  numSequence(n: number): Array<number> {
    return Array.from({ length: n }, (_, i) => i);
  }

  onSort(event: SortPropDir[]) {
    this.isLoading = true;
    const sortInfo = event[0]; // Get first sort
    const ascOrDesc = sortInfo.dir === 'asc' ? '' : '-';
    // Find the full column object to access orderingParam
    const column = this.columns.find((col) => col.prop === sortInfo.prop);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = (column as any)?.orderingParam ?? sortInfo.prop;

    // reset params to the active state from the url otherwise it appends removed params;
    const activeParams = this.route.snapshot.queryParams;
    this.queryParams = new HttpParams().appendAll(activeParams);

    this.queryParams = this.queryParams.set('ordering', ascOrDesc + value);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObject(this.queryParams),
      queryParamsHandling: 'merge',
    });
  }

  protected override getData(queryParams: HttpParams): Observable<TTableData> {
    if (this.suppressFetch) {
      return EMPTY;
    }
    return this.getDataCallback(queryParams);
  }
}
